/* ─────────────────────────────────────────────────────────────
 * dv-ritualmix.js — DinVeda Ritualmix Phase 1
 * Build: 2026-05-23-r7 (handle-fix + cache-buster)
 *
 * State machine + screen navigation + rules engine + submit
 * pipeline for the 8-screen Ritualmix wizard. Loaded conditionally
 * on /pages/ritualmix via layout/theme.liquid.
 *
 * Ingredient library comes from window.DVRM_INGREDIENTS, serialized
 * by sections/dv-ritualmix.liquid from `shop.metaobjects.ingredient`.
 *
 * Plan ref: silly-zooming-bumblebee.md M3–M5.
 * Spec ref: §6 (Customer UX Flow), §8 (Formula Rules Engine),
 *           §9 (Formula Templates).
 *
 * IMPORTANT — DVRM_FEE_VARIANT_ID is hardcoded below. If the
 * Prüfgebühr product gets re-created, update this constant.
 * ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ─── Constants ─────────────────────────────────────────
  // Phase 1: hidden €10 review-fee product — used when formula is amber/blocked
  // and customer needs a manual review. Created by
  // automation/create-ritualmix-fee-product.js (2026-05-17).
  var DVRM_FEE_VARIANT_ID = 57360127623499;

  // Phase 2 (native Shopify): hidden €34.90 Custom 500g product — green
  // formulas check out directly against this variant with the full formula
  // carried in line-item properties. No external validator needed; the
  // wizard's client-side rules engine + staff spot-check of instant-checkout
  // orders is the trust model. Created by
  // automation/create-ritualmix-custom-product.js (2026-05-19).
  var DVRM_CUSTOM_VARIANT_ID = 57373217947979;

  // Public launch posture: every submission goes through the €10 review
  // fee. Keep the custom product ID for a later phase, but do not route
  // customers to instant custom-blend checkout.
  var DVRM_PHASE2_INSTANT_CHECKOUT = false;

  var STORAGE_KEY = 'dv-ritualmix-state-v1';
  var STATE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

  // Screen IDs in order
  var SCREENS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];

  // Blocked-word list — runs on blend name and Wunschzutat text.
  // Spec §15 + §22. Keep lowercased.
  var BLOCKED_WORDS = [
    'bio', 'adaptogen', 'adaptogene', 'apothekerarchiv', 'apotheke',
    'schlaf', 'schlafmittel', 'stress', 'stresskiller',
    'detox', 'cleanse',
    'hormone', 'hormonal', 'pcos', 'fertility', 'fruchtbarkeit',
    'heil', 'heilung', 'heilt', 'heilen', 'cure', 'therapie',
    'therapeutisch', 'medizinisch', 'klinisch',
    'reizdarm', 'ibs', 'anxiety', 'depression', 'gastritis',
    'cortisol', 'antibiotic', 'diabetes',
    'anti-aging', 'anti aging',
    'abnehmen', 'fettverbrennung', 'gewichtsverlust',
    'schmerz', 'entzündung', 'entzundung',
    'wirkung', 'wirkt gegen', 'immun boost', 'energie boost', 'fokus boost'
  ];

  // Ritual-moment ↔ base affinity. Used in Screen 5 "Empfohlen" filter.
  // Keys match metaobject `ritual_moments` and `base` IDs in Screen 3.
  var BASE_TO_RITUAL_MOMENTS = {
    'red_fruit':    ['red_fruit', 'morning', 'daily_bowl', 'selfcare'],
    'cacao_spice':  ['evening_cacao', 'protein', 'morning'],
    'fiber_bowl':   ['fiber_bowl', 'daily_bowl'],
    'protein':      ['protein', 'daily_bowl'],
    'herbal_tea':   ['tea', 'after_meal', 'selfcare', 'evening_cacao'],
    'green_daily':  ['green_daily', 'morning', 'daily_bowl']
  };

  // Default formula templates per ritual type (spec §9 + plan M4).
  // Customer never edits %; backend ratio is derived from these ranges
  // and the intensity toggle (`mild`/`ausgewogen`/`intensiv`).
  var TEMPLATES = {
    'red_fruit': {
      roles: { base: [40, 60], support: [15, 30], bright: [5, 15], body: [5, 20] }
    },
    'cacao_spice': {
      roles: { base: [45, 70], body: [10, 25], spice_a: [2, 10], spice_b: [1, 6], spice_c: [1, 6] }
    },
    'fiber_bowl': {
      roles: { base: [40, 70], support: [10, 30], fruit: [5, 25], spice: [1, 8] }
    },
    'protein': {
      roles: { base: [60, 85], taste: [5, 20], body: [5, 20], spice: [1, 8] }
    },
    'herbal_tea': {
      roles: { base: [40, 70], secondary: [10, 30], spice: [1, 10], fresh: [5, 20] }
    },
    'green_daily': {
      roles: { base: [40, 60], fruit: [10, 30], bright: [5, 20], spice: [1, 8] }
    }
  };

  // ─── State ─────────────────────────────────────────────
  var state = loadState() || {
    screen: 's1',
    history: ['s1'],
    ritual_moment: null,        // morning | daily_bowl | after_meal | evening_cacao | protein | tea | selfcare
    base: null,                 // red_fruit | cacao_spice | fiber_bowl | protein | herbal_tea | green_daily
    taste: null,                // fruchtig | schokoladig | würzig | mild | frisch | herb | säuerlich | warm_aromatisch
    ingredients: [],            // array of metaobject handles
    intensity: 'ausgewogen',    // mild | ausgewogen | intensiv
    category_filter: 'all',     // category chip selection on Screen 5
    blend_name: '',
    wunschzutat: '',
    consent_privacy: false,
    consent_marketing: false,
    customer_email: '',
    formula_id: null,           // assigned at submit
    submitted_at: null,
    saved_at: Date.now()
  };

  // ─── Storage helpers ──────────────────────────────────
  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || !s.saved_at) return null;
      if (Date.now() - s.saved_at > STATE_MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      // Defensive: ensure ingredients is an array (legacy state may have
      // serialized it differently or be null) and prune orphan handles
      // that no longer exist in the current ingredient library. Without
      // this, a stale entry from an earlier test session can pin the cap
      // at 6 and silently block ALL new selections.
      if (!Array.isArray(s.ingredients)) s.ingredients = [];
      return s;
    } catch (e) { return null; }
  }

  // Strip any ingredient handle from saved state that doesn't match the
  // current window.DVRM_INGREDIENTS catalogue. Runs once at init AFTER
  // DVRM_INGREDIENTS is populated. Returns the number pruned.
  function pruneOrphanIngredients() {
    if (!state.ingredients || !state.ingredients.length) return 0;
    var lib = getIngredients();
    if (!lib.length) return 0;
    var validHandles = {};
    lib.forEach(function (i) { validHandles[i.handle] = true; });
    var before = state.ingredients.length;
    state.ingredients = state.ingredients.filter(function (h) { return validHandles[h]; });
    var pruned = before - state.ingredients.length;
    if (pruned > 0) {
      console.warn('[Ritualmix] pruned ' + pruned + ' orphan ingredient handle(s) from saved state');
      saveState();
    }
    return pruned;
  }

  function saveState() {
    try {
      state.saved_at = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* quota or private mode — skip */ }
  }

  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ─── DOM helpers ──────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // ─── Screen navigation ────────────────────────────────
  function goTo(screenId, opts) {
    opts = opts || {};
    if (SCREENS.indexOf(screenId) === -1) return;
    if (!opts.replace && state.screen !== screenId) {
      state.history.push(screenId);
    }
    state.screen = screenId;
    showScreen(screenId);
    updateProgress();
    onScreenEnter(screenId);
    saveState();
    if (window.scrollTo) window.scrollTo({ top: 0, behavior: 'smooth' });
    // Move keyboard / screen-reader focus to the new screen's heading
    // so users land in the right place. WCAG 2.4.3 (Focus Order).
    // The heading already has the screen's main title in editorial type,
    // which is also what a screen reader will announce.
    moveFocusToScreen(screenId);
  }

  // Find the new screen's primary heading and move focus to it. The
  // heading is given tabindex="-1" so it can receive programmatic
  // focus without being part of normal Tab order.
  function moveFocusToScreen(screenId) {
    setTimeout(function () {
      var screen = document.getElementById('dvrm-' + screenId);
      if (!screen) return;
      var heading = screen.querySelector('.dvrm__h, .dvrm__h1, h1, h2');
      if (!heading) return;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: true }); }
      catch (_) { heading.focus(); }
    }, 50);
  }

  function goBack() {
    if (state.history.length < 2) return;
    state.history.pop();
    var prev = state.history[state.history.length - 1] || 's1';
    state.screen = prev;
    showScreen(prev);
    updateProgress();
    onScreenEnter(prev);
    saveState();
  }

  // Hook to re-render the destination screen so it reflects current state
  function onScreenEnter(id) {
    if (id === 's2') renderGoals();
    else if (id === 's3') renderBases();
    else if (id === 's4') renderTastes();
    else if (id === 's5') renderIngredients();
    else if (id === 's6') renderBlendCheck();
    else if (id === 's7') renderNameForm();
    else if (id === 's8') { renderSummary(); renderSubmitGate(); }
  }

  function showScreen(id) {
    SCREENS.forEach(function (s) {
      var el = document.getElementById('dvrm-' + s);
      if (!el) return;
      if (s === id) el.classList.add('is-on');
      else el.classList.remove('is-on');
    });
  }

  function updateProgress() {
    var idx = SCREENS.indexOf(state.screen);
    var pct = idx <= 0 ? 0 : Math.round((idx / (SCREENS.length - 1)) * 100);
    $$('.dvrm__prog-fill').forEach(function (b) { b.style.width = pct + '%'; });
  }

  // ─── Ingredients library access ───────────────────────
  // Lazy-coerced + cached. Ensures every list-type field is a real
  // array so downstream `.indexOf()` calls can't throw — even if a
  // future Liquid change reverts the serialization to objects.
  var _ingredientsCache = null;
  function getIngredients() {
    if (_ingredientsCache) return _ingredientsCache;
    var raw = (window.DVRM_INGREDIENTS && Array.isArray(window.DVRM_INGREDIENTS)) ? window.DVRM_INGREDIENTS : [];
    var LIST_FIELDS = ['taste_profile', 'ritual_moments', 'format_allowed', 'allergen_flags'];
    _ingredientsCache = raw.map(function (ing) {
      if (!ing || typeof ing !== 'object') return ing;
      LIST_FIELDS.forEach(function (k) {
        if (!Array.isArray(ing[k])) ing[k] = [];
      });
      return ing;
    });
    return _ingredientsCache;
  }

  function ingredientByHandle(h) {
    var all = getIngredients();
    for (var i = 0; i < all.length; i++) if (all[i].handle === h) return all[i];
    return null;
  }

  // ─── Rules engine ─────────────────────────────────────
  // Spec §8. Each rule returns null on pass, or { code, message } on fail.

  function validateCount() {
    var n = state.ingredients.length;
    if (n < 3) return { code: 'too_few', message: 'Wähle mindestens 3 Zutaten.' };
    if (n > 6) return { code: 'too_many', message: 'Maximal 6 Zutaten.' };
    return null;
  }

  function validateAllGreen() {
    for (var i = 0; i < state.ingredients.length; i++) {
      var ing = ingredientByHandle(state.ingredients[i]);
      if (!ing) return { code: 'unknown_ingredient', message: 'Eine ausgewählte Zutat ist nicht mehr verfügbar.' };
      if (ing.risk_zone !== 'green') {
        return { code: 'non_green', message: 'Diese Mischung enthält Zutaten, die wir manuell prüfen müssen.' };
      }
    }
    return null;
  }

  function validateBlockedWords(text) {
    if (!text) return null;
    var lower = String(text).toLowerCase();
    for (var i = 0; i < BLOCKED_WORDS.length; i++) {
      var bw = BLOCKED_WORDS[i];
      var pattern = new RegExp('\\b' + bw.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') + '\\b');
      if (pattern.test(lower)) {
        return { code: 'blocked_word', message: 'Bitte vermeide medizinische oder Heilversprechen-Wörter (gefunden: „' + bw + '").' };
      }
    }
    return null;
  }

  function computeAllergens() {
    var flags = {};
    state.ingredients.forEach(function (h) {
      var ing = ingredientByHandle(h);
      if (!ing || !ing.allergen_flags) return;
      ing.allergen_flags.forEach(function (a) { flags[a] = true; });
    });
    return Object.keys(flags);
  }

  function computeCaffeine() {
    for (var i = 0; i < state.ingredients.length; i++) {
      var ing = ingredientByHandle(state.ingredients[i]);
      if (ing && ing.caffeine_flag) return true;
    }
    return false;
  }

  function computeStatus() {
    // Order matters: hard failures first
    var v = validateCount();
    if (v) return { status: 'blocked', reason: v };
    v = validateAllGreen();
    if (v) {
      if (v.code === 'non_green') return { status: 'amber_review_required', reason: v };
      return { status: 'blocked', reason: v };
    }
    var nameCheck = validateBlockedWords(state.blend_name);
    if (nameCheck) return { status: 'blocked', reason: { code: 'blocked_name', message: nameCheck.message } };
    var wzCheck = validateBlockedWords(state.wunschzutat);
    if (wzCheck) return { status: 'blocked', reason: { code: 'blocked_wunschzutat', message: wzCheck.message } };
    return { status: 'green_request', reason: null };
  }

  /**
   * Pick deterministic ratios from the formula template + selected ingredients.
   * Returns { handle: percentage } summing to 100.
   * Simple Phase-1 algorithm: each ingredient gets the midpoint of its allowed
   * range (clamped to the ingredient's max_pct from the metaobject), normalized
   * to sum to 100. Intensity (mild/ausgewogen/intensiv) shifts the spice/aromatic
   * components within their bands.
   */
  function pickTemplateRatios() {
    var ings = state.ingredients.map(ingredientByHandle).filter(Boolean);
    if (!ings.length) return {};
    var template = TEMPLATES[state.base] || null;
    var intensityShift = state.intensity === 'mild' ? -0.3 : state.intensity === 'intensiv' ? 0.3 : 0;

    // Initial weights from each ingredient's own max_pct (caps the upper bound).
    var weights = ings.map(function (ing) {
      var base = (ing.min_pct || 5) + ((ing.max_pct || 25) - (ing.min_pct || 5)) * (0.5 + intensityShift / 2);
      base = Math.max(ing.min_pct || 1, Math.min(ing.max_pct || 100, base));
      return base;
    });
    // Normalize to sum to 100, rounded to whole numbers, residual carried into the base
    var sum = weights.reduce(function (a, b) { return a + b; }, 0);
    var ratios = weights.map(function (w) { return Math.max(1, Math.round((w / sum) * 100)); });
    var diff = 100 - ratios.reduce(function (a, b) { return a + b; }, 0);
    // assign residual to whichever ingredient has the largest weight (typically the base)
    var maxIdx = 0;
    for (var i = 1; i < ratios.length; i++) if (ratios[i] > ratios[maxIdx]) maxIdx = i;
    ratios[maxIdx] += diff;

    var out = {};
    ings.forEach(function (ing, i) { out[ing.handle] = ratios[i]; });
    return out;
  }

  function composeFormulaJSON() {
    var ratios = pickTemplateRatios();
    var snap = state.ingredients.map(function (h) {
      var ing = ingredientByHandle(h) || {};
      return {
        handle: h,
        customer_name_de: ing.customer_name_de || h,
        label_name_de: ing.label_name_de || ing.customer_name_de || h,
        supplier: ing.supplier_name_internal || '',
        risk_zone: ing.risk_zone || 'unknown',
        percentage: ratios[h] || 0,
        max_g_per_serving: ing.max_g_per_serving || null
      };
    });
    var status = computeStatus();
    return {
      schema_version: 1,
      formula_id: state.formula_id || null,
      ritual_moment: state.ritual_moment,
      base: state.base,
      taste: state.taste,
      intensity: state.intensity,
      ingredients: snap,
      allergens: computeAllergens(),
      caffeine: computeCaffeine(),
      pack_size_g: 500,
      blend_name: state.blend_name,
      wunschzutat_text: state.wunschzutat || null,
      status: status.status,
      created_at: new Date().toISOString()
    };
  }

  // ─── Renderers (Phase 1 — Screens 1–4 active; 5–7 will fill in M4; 8 in M5) ───

  function renderGoals() {
    var grid = $('#dvrm-goals');
    if (!grid) return;
    $$('button', grid).forEach(function (btn) {
      var goal = btn.getAttribute('data-goal');
      btn.classList.toggle('is-on', state.ritual_moment === goal);
    });
    var next = $('#dvrm-goal-next');
    if (next) next.disabled = !state.ritual_moment;
    var note = $('#dvrm-goal-note');
    if (note) note.textContent = state.ritual_moment ?
      'Weiter zur Basis-Auswahl.' :
      'Wähle einen Ritualmoment, um fortzufahren.';
  }

  function renderBases() {
    var grid = $('#dvrm-bases');
    if (!grid) return;
    $$('button', grid).forEach(function (btn) {
      var base = btn.getAttribute('data-base');
      btn.classList.toggle('is-on', state.base === base);
    });
    var next = $('#dvrm-base-next');
    if (next) next.disabled = !state.base;
    var note = $('#dvrm-base-note');
    if (note) note.textContent = state.base ?
      'Weiter zur Geschmacks-Auswahl.' :
      'Wähle eine Basis, um fortzufahren.';
  }

  function renderTastes() {
    var grid = $('#dvrm-tastes');
    if (!grid) return;
    $$('button', grid).forEach(function (btn) {
      var taste = btn.getAttribute('data-taste');
      btn.classList.toggle('is-on', state.taste === taste);
    });
    var next = $('#dvrm-taste-next');
    if (next) next.disabled = !state.taste;
    var note = $('#dvrm-taste-note');
    if (note) note.textContent = state.taste ?
      'Weiter zur Zutaten-Auswahl.' :
      'Wähle eine Geschmacksrichtung, um fortzufahren.';
  }

  // ─── Screen 5: Ingredient picker ──────────────────────
  // Category chip → predicate. Each predicate takes one ingredient
  // metaobject row and returns true if it matches the chip.
  var CATEGORY_PREDICATES = {
    all: function (ing) {
      // "Empfohlen" — match the current state.ritual_moment or base affinity.
      // If nothing is selected yet, show every green ingredient.
      var moments = (ing.ritual_moments || []);
      if (state.ritual_moment && moments.indexOf(state.ritual_moment) !== -1) return true;
      if (state.base) {
        var allowed = BASE_TO_RITUAL_MOMENTS[state.base] || [];
        for (var i = 0; i < allowed.length; i++) {
          if (moments.indexOf(allowed[i]) !== -1) return true;
        }
      }
      // Fallback: show all if no signal yet
      return !state.ritual_moment && !state.base;
    },
    tea: function (ing) {
      var fmt = ing.format_allowed || [];
      return ing.ingredient_form === 'cut-herb' || fmt.indexOf('tea') !== -1;
    },
    powder: function (ing) {
      return ing.ingredient_form === 'powder' || ing.ingredient_form === 'whole';
    },
    spice: function (ing) {
      // Kakao & Gewürze: spices, kakao, ginger, cinnamon, cardamom, turmeric
      var spices = ['kakao', 'ceylon-zimt', 'kardamom', 'ingwer', 'kurkuma', 'fenchel'];
      return spices.indexOf(ing.handle) !== -1;
    },
    fruit_color: function (ing) {
      // Früchte & Farbe: red fruits, hibiscus, beetroot
      var fruits = ['aronia', 'hagebutte', 'acerola-extract-powder',
                    'baobab-fruit-powder', 'beetroot-powder', 'hibiskus'];
      return fruits.indexOf(ing.handle) !== -1;
    },
    protein: function (ing) {
      var p = (ing.ritual_moments || []);
      return p.indexOf('protein') !== -1;
    },
    fiber_bowl: function (ing) {
      var p = (ing.ritual_moments || []);
      return p.indexOf('fiber_bowl') !== -1;
    }
  };

  function ingredientCard(ing) {
    var isSelected = state.ingredients.indexOf(ing.handle) !== -1;
    var atCap = !isSelected && state.ingredients.length >= 6;
    var isAmber = ing.risk_zone === 'amber';
    var classes = ['dvrm__ing'];
    if (isSelected) classes.push('is-on');
    if (atCap) classes.push('is-locked');
    if (isAmber) classes.push('is-amber');
    var taste = ing.taste_aroma_de || (ing.taste_profile || []).slice(0, 3).join(' · ');
    var formLabel = ({ powder: 'Pulver', 'cut-herb': 'Kräuter', whole: 'Ganz', tea: 'Tee', spice: 'Gewürz', seeds: 'Samen', flakes: 'Flocken' })[ing.ingredient_form] || ing.ingredient_form;

    return (
      '<div class="' + classes.join(' ') + '" data-handle="' + ing.handle + '" data-risk="' + escapeHtml(ing.risk_zone || 'green') + '">' +
      (isAmber
        ? '<span class="dvrm__ing-tag dvrm__ing-tag--amber" title="Diese Zutat verlangt eine manuelle Prüfung durch DinVeda">Nur mit DinVeda-Prüfung</span>'
        : (formLabel ? '<span class="dvrm__ing-tag">' + escapeHtml(formLabel) + '</span>' : '')) +
      '<span class="dvrm__ing-name">' + escapeHtml(ing.customer_name_de || ing.handle) + '</span>' +
      (taste ? '<span class="dvrm__ing-taste">' + escapeHtml(taste) + '</span>' : '') +
      '<span class="dvrm__ing-teaser">' + escapeHtml(ing.teaser_de || '') + '</span>' +
      (isAmber
        ? '<span class="dvrm__ing-amber-note">Wenn du diese Zutat wählst, prüfen wir deine Mischung manuell (10 € Prüfgebühr, anrechenbar).</span>'
        : '') +
      '<div class="dvrm__ing-actions">' +
      '  <button type="button" class="dvrm__ing-more" data-more="' + ing.handle + '">Mehr erfahren</button>' +
      '  <button type="button" class="dvrm__ing-toggle" data-toggle="' + ing.handle + '">' +
      (isSelected ? 'Entfernen' :
       atCap ? 'Max. 6' : 'Hinzufügen') +
      '  </button>' +
      '</div>' +
      '</div>'
    );
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Full grid rebuild — only used when the visible set of ingredients
  // changes (category filter switch, initial render). Toggling
  // selection state should NOT rebuild — see updateIngredientsState
  // below for the cheap targeted update. Previously every tap blew
  // away all 22 cards via innerHTML, which made rapid taps unreliable
  // (the second tap landed on a detached DOM node).
  function renderIngredients() {
    var grid = $('#dvrm-ings-grid');
    if (!grid) return;
    var pred = CATEGORY_PREDICATES[state.category_filter] || CATEGORY_PREDICATES.all;
    // Defensive filter: a single bad predicate row must NOT crash the
    // entire grid render.
    var rows = getIngredients().filter(function (ing) {
      try { return pred(ing); }
      catch (e) {
        console.warn('[Ritualmix] predicate error for', ing && ing.handle, e);
        return false;
      }
    });
    // Stable order: alphabetical by customer_name_de. (Selected-first
    // sorting was removed because it reorders cards on every toggle,
    // which is jarring on mobile — selected cards visually "jump" to
    // the top, confusing the user about which card they just tapped.)
    rows.sort(function (a, b) {
      return (a.customer_name_de || '').localeCompare(b.customer_name_de || '', 'de');
    });
    if (!rows.length) {
      grid.innerHTML = '<div class="dvrm__stub" style="grid-column:1/-1"><strong>Keine Treffer in dieser Kategorie.</strong>Wechsle auf <em>Empfohlen</em>, um alle passenden Zutaten zu sehen.</div>';
    } else {
      grid.innerHTML = rows.map(ingredientCard).join('');
    }
    updateCategoryChips();
    updateSelbar();
  }

  // Cheap, targeted DOM update. Called after every toggleIngredient.
  // Updates only:
  //   - each card's .is-on / .is-locked class
  //   - each toggle button's label
  //   - the selbar count + Weiter button state
  // No innerHTML rebuild → previously-clicked DOM nodes stay attached
  // → rapid taps all register correctly even on slow mobile.
  function updateIngredientsState() {
    var grid = $('#dvrm-ings-grid');
    if (!grid) return;
    var selected = state.ingredients;
    var atCap = selected.length >= 6;
    grid.querySelectorAll('.dvrm__ing[data-handle]').forEach(function (card) {
      var h = card.getAttribute('data-handle');
      var isSel = selected.indexOf(h) !== -1;
      card.classList.toggle('is-on', isSel);
      card.classList.toggle('is-locked', !isSel && atCap);
      var btn = card.querySelector('.dvrm__ing-toggle');
      if (btn) {
        btn.textContent = isSel ? 'Entfernen' : (!isSel && atCap ? 'Max. 6' : 'Hinzufügen');
      }
    });
    updateSelbar();
  }

  function updateCategoryChips() {
    $$('#dvrm-cats button').forEach(function (b) {
      b.classList.toggle('is-on', b.getAttribute('data-cat') === state.category_filter);
    });
  }

  function updateSelbar() {
    var n = state.ingredients.length;
    var st = $('#dvrm-selbar-text');
    if (st) st.textContent = n + ' von 6 Zutaten ausgewählt';
    var next = $('#dvrm-ings-next');
    if (next) {
      var ok = n >= 3 && n <= 6;
      next.disabled = !ok;
      next.title = ok ? '' : (n < 3 ? 'Mindestens 3 Zutaten wählen' : 'Maximal 6 Zutaten');
    }
  }

  function toggleIngredient(handle) {
    var idx = state.ingredients.indexOf(handle);
    if (idx === -1) {
      if (state.ingredients.length >= 6) {
        // Surface the cap visibly — a silent return makes the user think
        // "Hinzufügen" is broken. Flash the selbar text + nudge.
        flashSelbar('Max. 6 Zutaten — entferne eine, um zu wechseln.');
        return;
      }
      var ing = ingredientByHandle(handle);
      if (!ing) {
        // Orphan handle (rare; shouldn't happen post-pruning) — surface it
        console.warn('[Ritualmix] toggleIngredient: unknown handle', handle);
        flashSelbar('Diese Zutat ist nicht mehr verfügbar.');
        return;
      }
      // Phase 3: amber ingredients ARE selectable — they just force the
      // formula into the manual review flow (€10 review fee). The amber
      // card already shows a clear note: "Wenn du diese Zutat wählst,
      // prüfen wir deine Mischung manuell". Only `red` (banned) is rejected.
      if (ing.risk_zone === 'red') {
        flashSelbar('Diese Zutat ist aktuell nicht wählbar.');
        return;
      }
      state.ingredients.push(handle);
    } else {
      state.ingredients.splice(idx, 1);
    }
    saveState();
    // Cheap targeted update — no innerHTML rebuild, no detached nodes.
    updateIngredientsState();
  }

  // Visual feedback for selbar — flashes a message + amber border briefly.
  // Defensive against missing selbar (e.g. if user is not on screen 5).
  function flashSelbar(msg) {
    var sel = $('#dvrm-selbar');
    var txt = $('#dvrm-selbar-text');
    if (!sel || !txt) return;
    var original = txt.textContent;
    txt.textContent = msg;
    sel.classList.add('is-warn');
    clearTimeout(flashSelbar._t);
    flashSelbar._t = setTimeout(function () {
      sel.classList.remove('is-warn');
      // Restore the current count (in case it changed during the timeout)
      txt.textContent = (state.ingredients.length || 0) + ' von 6 Zutaten ausgewählt';
    }, 2200);
  }

  // ─── Screen 5: Mehr-erfahren modal ────────────────────
  function openModal(handle) {
    var ing = ingredientByHandle(handle);
    if (!ing) return;
    var modal = $('#dvrm-modal');
    if (!modal) return;
    $('#dvrm-modal-eyebrow').textContent = 'Zutat · ' + (ing.ingredient_form === 'cut-herb' ? 'Kräuter' : 'Pulver');
    $('#dvrm-modal-title').textContent = ing.customer_name_de || ing.handle;

    var rows = [];
    if (ing.taste_aroma_de || (ing.taste_profile || []).length) {
      rows.push({ k: 'Geschmack', v: ing.taste_aroma_de || (ing.taste_profile || []).join(' · ') });
    }
    if (ing.usage_idea_de) {
      rows.push({ k: 'Verwendung', v: ing.usage_idea_de });
    }
    if (ing.botanical_name) {
      rows.push({ k: 'Botanik', v: ing.botanical_name });
    }
    if (ing.label_name_de && ing.label_name_de !== ing.customer_name_de) {
      rows.push({ k: 'Etikett-Name', v: ing.label_name_de });
    }

    var html = '';
    if (ing.teaser_de) html += '<p>' + escapeHtml(ing.teaser_de) + '</p>';
    if (rows.length) {
      html += rows.map(function (r) {
        return '<div class="dvrm__modal-row"><span class="dvrm__modal-k">' + escapeHtml(r.k) + '</span><span class="dvrm__modal-v">' + escapeHtml(r.v) + '</span></div>';
      }).join('');
    }
    html += '<p style="font-size:12px;opacity:0.75;margin-top:8px">Hinweise auf Studien dienen der allgemeinen Einordnung der Zutat und sind nicht automatisch auf einen individuellen Ritualmix übertragbar.</p>';
    $('#dvrm-modal-body').innerHTML = html;

    // Toggle button state
    var toggle = $('#dvrm-modal-toggle');
    var inMix = state.ingredients.indexOf(handle) !== -1;
    toggle.setAttribute('data-handle', handle);
    var atCap = !inMix && state.ingredients.length >= 6;
    toggle.disabled = atCap;
    toggle.textContent = inMix ? 'Aus Mix entfernen' : (atCap ? 'Max. 6 erreicht' : 'In Mix aufnehmen');

    // Save the element the user was on so we can restore focus on close.
    // Keyboard + screen-reader users would otherwise be dumped back at
    // <body> when the modal closes.
    _modalOpener = document.activeElement;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    // Use a data attribute (not inline style) so a JS error inside the
    // modal can't permanently scroll-lock the body. A CSS rule then
    // sets overflow:hidden, but the lock is recoverable by removing
    // the attribute (e.g. on navigation).
    document.documentElement.setAttribute('data-dvrm-modal-open', '');

    // Move focus into the modal — first the close button, which is
    // a safe entry point. Defer to next tick so the panel is paintable.
    setTimeout(function () {
      var closeBtn = $('#dvrm-modal-close');
      if (closeBtn && closeBtn.focus) {
        try { closeBtn.focus({ preventScroll: false }); } catch (_) { closeBtn.focus(); }
      }
    }, 0);
  }

  // Focus trap: when modal is open, Tab/Shift+Tab cycles ONLY within
  // the modal panel. Without this, keyboard users tab right out of the
  // modal and into the (visually hidden) page underneath.
  function trapModalFocus(e) {
    if (e.key !== 'Tab') return;
    var modal = $('#dvrm-modal');
    if (!modal || modal.hidden) return;
    var focusables = modal.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  var _modalOpener = null;

  function closeModal() {
    var modal = $('#dvrm-modal');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.removeAttribute('data-dvrm-modal-open');
    // Restore focus to the element that opened the modal so keyboard
    // users continue from where they left off.
    if (_modalOpener && _modalOpener.focus) {
      try { _modalOpener.focus(); } catch (_) {}
    }
    _modalOpener = null;
  }

  // ─── Screen 6: Blend Check ────────────────────────────
  function tasteLabelDE() {
    var combo = [];
    if (state.taste) combo.push(labelForTaste(state.taste));
    if (state.intensity) combo.push(state.intensity);
    return combo.length ? combo.join(' · ') : 'Ausgewogen';
  }
  function formatLabelDE() {
    var moments = state.ritual_moment || '';
    if (moments === 'tea' || moments === 'after_meal') return 'Tee / warmer Aufguss';
    if (moments === 'evening_cacao') return 'Warmer Drink / Kakao';
    if (moments === 'protein' || moments === 'daily_bowl') return 'Smoothie / Bowl / Shake';
    if (moments === 'fiber_bowl') return 'Bowl / Porridge';
    if (moments === 'green_daily' || moments === 'morning') return 'Smoothie / Drink';
    return 'Vielseitig';
  }
  function renderBlendCheck() {
    // Intensity pills
    $$('#dvrm-intensities button').forEach(function (b) {
      b.classList.toggle('is-on', b.getAttribute('data-intensity') === state.intensity);
    });

    $('#dvrm-check-taste').textContent = tasteLabelDE();
    $('#dvrm-check-count').textContent = state.ingredients.length + ' von 6';
    $('#dvrm-check-format').textContent = formatLabelDE();

    var allergens = computeAllergens();
    var caf = computeCaffeine();
    var notes = [];
    if (allergens.length) notes.push('Allergen-Hinweise: ' + allergens.join(', '));
    if (caf) notes.push('Enthält koffeinhaltige Zutat (Kakao)');
    $('#dvrm-check-allergens').textContent = notes.length ? notes.join(' · ') : 'Keine';

    var st = computeStatus();
    var box = $('#dvrm-check-status');
    box.classList.remove('dvrm__check-status--green', 'dvrm__check-status--amber', 'dvrm__check-status--red');
    if (st.status === 'green_request') {
      box.classList.add('dvrm__check-status--green');
      box.textContent = 'Deine Mischung ist prüfbar. Wir kontrollieren Rezeptur, Etikett und Verfügbarkeit vor der finalen Freigabe.';
    } else if (st.status === 'amber_review_required') {
      box.classList.add('dvrm__check-status--amber');
      box.textContent = 'Deine Mischung enthält Zutaten, die wir manuell prüfen (z. B. Ashwagandha, Triphala). Du sendest eine Anfrage mit 10 € Prüfgebühr — wir melden uns in 1–2 Werktagen mit der finalen Freigabe. Die Gebühr wird auf deine Bestellung angerechnet.';
    } else {
      box.classList.add('dvrm__check-status--red');
      box.textContent = (st.reason && st.reason.message) || 'Diese Kombination können wir aktuell nicht anbieten. Bitte passe deine Zutaten an.';
    }

    var next = $('#dvrm-check-next');
    if (next) {
      next.disabled = st.status === 'blocked';
      next.title = next.disabled ? (st.reason && st.reason.message) || '' : '';
    }
    var note = $('#dvrm-check-note');
    if (note) note.textContent = st.status === 'blocked' ?
      'Bitte passe deine Auswahl an, um fortzufahren.' :
      'Weiter zu Name & Wunschzutat.';
  }

  // ─── Screen 7: Name + Wunschzutat ─────────────────────
  var SUGGESTED_NAMES = [
    'Ruby Morning', 'Cacao Evening', 'Golden Bowl', 'Fennel Garden',
    'Berry Glow', 'Daily Green', 'Spiced Cacao', 'Warm Garden',
    'Rose Bowl', 'Gentle Spice', 'Bright Morning', 'Forest Daily'
  ];
  function pickSuggestedName() {
    // Bias suggestion by taste/base if possible
    var pool = SUGGESTED_NAMES.slice();
    if (state.taste === 'fruchtig' || state.base === 'red_fruit') {
      pool = ['Ruby Morning', 'Berry Glow', 'Bright Morning', 'Rose Bowl'];
    } else if (state.taste === 'schokoladig' || state.base === 'cacao_spice') {
      pool = ['Cacao Evening', 'Spiced Cacao', 'Warm Garden', 'Gentle Spice'];
    } else if (state.base === 'protein') {
      pool = ['Golden Bowl', 'Daily Green'];
    } else if (state.base === 'herbal_tea' || state.ritual_moment === 'tea') {
      pool = ['Fennel Garden', 'Gentle Spice', 'Forest Daily'];
    } else if (state.base === 'green_daily') {
      pool = ['Daily Green', 'Forest Daily', 'Bright Morning'];
    }
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function setFormError(elId, errEl, msg) {
    var input = $('#' + elId);
    var err = errEl;
    if (msg) {
      input && input.classList.add('dvrm__input--error');
      if (err) { err.textContent = msg; err.hidden = false; }
    } else {
      input && input.classList.remove('dvrm__input--error');
      if (err) { err.textContent = ''; err.hidden = true; }
    }
  }

  function renderNameForm() {
    var nameInput = $('#dvrm-name');
    var wzInput = $('#dvrm-wunschzutat');
    if (nameInput && nameInput.value !== state.blend_name) nameInput.value = state.blend_name || '';
    if (wzInput && wzInput.value !== state.wunschzutat) wzInput.value = state.wunschzutat || '';

    var nameErr = $('#dvrm-name-error');
    var wzErr = $('#dvrm-wunschzutat-error');

    var nameValid = false;
    if (!state.blend_name || state.blend_name.trim().length < 2) {
      setFormError('dvrm-name', nameErr, '');
    } else {
      var nv = validateBlockedWords(state.blend_name);
      if (nv) {
        setFormError('dvrm-name', nameErr, nv.message);
      } else {
        setFormError('dvrm-name', nameErr, '');
        nameValid = true;
      }
    }

    var wzValid = true;
    if (state.wunschzutat) {
      var wv = validateBlockedWords(state.wunschzutat);
      if (wv) {
        setFormError('dvrm-wunschzutat', wzErr, wv.message);
        wzValid = false;
      } else {
        setFormError('dvrm-wunschzutat', wzErr, '');
      }
    } else {
      setFormError('dvrm-wunschzutat', wzErr, '');
    }

    var next = $('#dvrm-name-next');
    if (next) next.disabled = !(nameValid && wzValid);
    var note = $('#dvrm-name-note');
    if (note) note.textContent = nameValid ? 'Weiter zur Zusammenfassung.' : 'Gib einen Namen ein, um fortzufahren.';
  }

  // ─── Screen 8: Summary + Submit ─────────────────────
  function uuidv4() {
    if (window.crypto && window.crypto.randomUUID) {
      try { return window.crypto.randomUUID(); } catch (e) {}
    }
    return 'dvrm-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 10) + '-' +
      Math.random().toString(36).slice(2, 10);
  }

  function renderSummary() {
    var box = $('#dvrm-summary');
    if (!box) return;
    var ratios = pickTemplateRatios();
    var ingredients = state.ingredients.map(function (h) {
      var ing = ingredientByHandle(h);
      var pct = ratios[h];
      var name = (ing && ing.customer_name_de) || h;
      return name + (pct ? ' ' + pct + '%' : '');
    }).join(' · ');

    var rows = [
      { k: 'Name', v: escapeHtml(state.blend_name || '—') },
      { k: 'Ritualmoment', v: escapeHtml(labelForRitualMoment(state.ritual_moment) || '—') },
      { k: 'Basis', v: escapeHtml(labelForBase(state.base) || '—') },
      { k: 'Geschmack', v: escapeHtml(labelForTaste(state.taste) || '—') + ' · ' + escapeHtml(state.intensity || 'ausgewogen') },
      { k: 'Zutaten', v: escapeHtml(ingredients) || '—' }
    ];
    if (state.wunschzutat) {
      rows.push({ k: 'Wunschzutat zur Prüfung', v: '<em style="font-style:italic">' + escapeHtml(state.wunschzutat) + '</em> <span style="font-size:12px;opacity:0.7;display:block;margin-top:4px">Nicht Teil der aktuellen Mischung. Wir prüfen Verfügbarkeit für zukünftige Aufnahme.</span>' });
    }
    rows.push({ k: 'Menge', v: '500 g' });

    // Public launch: every Ritualmix starts as a review request.
    rows.push({
      k: 'Prüfgebühr',
      v: '<strong>10,00 €</strong> · <span style="font-size:12px;opacity:0.8;display:block;margin-top:4px">Diese Mischung prüfen wir manuell. Die Gebühr wird auf deine finale Bestellung angerechnet, wenn dein Ritualmix freigegeben und bestellt wird.</span>'
    });

    box.innerHTML = rows.map(function (r) {
      return '<div class="dvrm__summary-row"><span class="dvrm__summary-k">' + escapeHtml(r.k) + '</span><span class="dvrm__summary-v">' + r.v + '</span></div>';
    }).join('');
  }

  function validateSubmit() {
    // Returns null on pass, error message string on fail
    var st = computeStatus();
    if (st.status === 'blocked') {
      return (st.reason && st.reason.message) || 'Diese Mischung können wir aktuell nicht annehmen.';
    }
    var sectionEl = document.getElementById('dvrm');
    var loggedInEmail = sectionEl && sectionEl.getAttribute('data-customer-email');
    if (!loggedInEmail) {
      var emailEl = $('#dvrm-email');
      var em = emailEl && emailEl.value && emailEl.value.trim();
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        return 'Bitte gib eine gültige E-Mail-Adresse ein.';
      }
      state.customer_email = em;
    } else {
      state.customer_email = loggedInEmail;
    }
    var p = $('#dvrm-privacy');
    if (!p || !p.checked) {
      return 'Bitte bestätige die Datenschutzhinweise.';
    }
    state.consent_privacy = true;
    state.consent_marketing = !!($('#dvrm-marketing') && $('#dvrm-marketing').checked);
    return null;
  }

  function renderSubmitGate() {
    var btn = $('#dvrm-submit-btn');
    var note = $('#dvrm-submit-note');
    if (!btn) return;
    var err = validateSubmit();
    // IMPORTANT: do NOT use the `disabled` attribute on the submit button.
    // Disabled buttons don't fire click events on mobile, which means a tap
    // feels unresponsive ("I tapped but nothing happened"). Instead, use a
    // `is-blocked` class for visual styling, and rely on submitRequest()
    // to validate, show the error inline, and scroll to the missing field.
    btn.disabled = false;
    if (err) btn.classList.add('is-blocked');
    else btn.classList.remove('is-blocked');
    if (note) {
      if (err) note.textContent = err;
      else note.textContent = 'Bereit zum Senden. Du wirst zum sicheren Shopify-Checkout weitergeleitet.';
    }
  }

  function submitRequest() {
    var err = validateSubmit();
    if (err) {
      var noteEl = $('#dvrm-submit-note');
      if (noteEl) noteEl.textContent = err;
      // Smart mobile UX: scroll the customer to the missing field and flash
      // it so they instantly see what to fix. Prevents the "tapped but
      // nothing happened" abandonment pattern on phones.
      var target = null;
      if (/E-?Mail/i.test(err)) target = $('#dvrm-email');
      else if (/Datenschutz/i.test(err)) target = $('#dvrm-privacy');
      else if (/Mischung/i.test(err)) target = $('#dvrm-summary');
      if (target) {
        try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        catch (_) { target.scrollIntoView(); }
        var row = target.closest('.dvrm__checkbox-row, .dvrm__form-block, .dvrm__summary') || target;
        row.classList.add('dvrm__flash');
        setTimeout(function () { row.classList.remove('dvrm__flash'); }, 1400);
        if (target.focus) setTimeout(function () { try { target.focus(); } catch (_) {} }, 350);
      }
      return;
    }
    var btn = $('#dvrm-submit-btn');
    var note = $('#dvrm-submit-note');
    if (btn) { btn.disabled = true; btn.textContent = 'Wird gesendet…'; }

    // Generate a stable formula_id if missing
    if (!state.formula_id) state.formula_id = uuidv4();
    state.submitted_at = new Date().toISOString();
    saveState();

    var formula = composeFormulaJSON();

    // Public launch: all formulas continue to the €10 review-fee path.
    // The hidden Custom 500g product remains reserved for a later phase.
    var isGreen = formula.status === 'green_request';
    var goInstantCheckout = isGreen && DVRM_PHASE2_INSTANT_CHECKOUT;
    var phaseLabel = goInstantCheckout ? '2' : '1';
    var variantId = goInstantCheckout ? DVRM_CUSTOM_VARIANT_ID : DVRM_FEE_VARIANT_ID;

    if (note) {
      note.textContent = 'Wir bereiten deine Prüfanfrage vor …';
    }

    // Pretty-printed German summary for the visible "Zutaten" line-item property
    var ingSummary = formula.ingredients.map(function (i) {
      return i.customer_name_de + ' ' + i.percentage + '%';
    }).join(' · ');

    var props = {
      'Blend Name': state.blend_name,
      'Ritualmoment': labelForRitualMoment(state.ritual_moment) || '',
      'Basis': labelForBase(state.base) || '',
      'Geschmack': (labelForTaste(state.taste) || '') + ' · ' + state.intensity,
      'Zutaten': ingSummary,
      'Menge': '500 g'
    };
    if (state.wunschzutat) props['Wunschzutat zur Prüfung'] = state.wunschzutat;

    // Hidden underscored properties — preserved on the order, suppressed from
    // the customer's order-confirmation email but visible in Shopify Admin.
    props._formula_id = state.formula_id;
    props._formula_json = JSON.stringify(formula);
    props._ritualmix_status = goInstantCheckout ? 'green_auto_approved' : formula.status;
    props._ritualmix_schema_version = String(formula.schema_version);
    props._ritualmix_phase = phaseLabel;
    props._ritualmix_flow = goInstantCheckout ? 'instant_checkout' : 'review_fee';
    if (!goInstantCheckout) props._deposit_creditable = 'true';
    props._customer_email = state.customer_email;
    props._consent_marketing = state.consent_marketing ? 'true' : 'false';
    props._submitted_at = state.submitted_at;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: props }] })
    })
      .then(function (r) {
        if (!r.ok) return r.text().then(function (t) { throw new Error('cart/add failed: ' + r.status + ' ' + t); });
        return r.json();
      })
      .then(function () {
        // For anonymous flows, attach email + formula ID to cart attributes so
        // they survive into the order even before the customer logs in.
        var section = document.getElementById('dvrm');
        var loggedInEmail = section && section.getAttribute('data-customer-email');
        if (loggedInEmail) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Ritualmix Email': state.customer_email,
              'Ritualmix Formula ID': state.formula_id,
              'Ritualmix Phase': phaseLabel
            }
          })
        });
      })
      .then(function () {
        clearState();
        window.location.href = '/checkout';
      })
      .catch(function (e) {
        console.error('[Ritualmix submit]', e);
        if (note) note.textContent = 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut oder schreib uns an info@greenpathventure.com.';
        if (btn) { btn.disabled = false; btn.textContent = 'Erneut versuchen'; }
      });
  }

  // ─── Resume banner ───────────────────────────────────
  function maybeShowResumeBanner() {
    if (state.screen === 's1' && state.history.length <= 1) return;
    var banner = $('#dvrm-resume');
    if (!banner) return;
    var hasProgress = !!(state.ritual_moment || state.base || state.taste || (state.ingredients || []).length);
    if (!hasProgress) return;
    var summary = [];
    if (state.ritual_moment) summary.push(labelForRitualMoment(state.ritual_moment));
    if (state.base) summary.push(labelForBase(state.base));
    if (state.taste) summary.push(labelForTaste(state.taste));
    if (state.ingredients && state.ingredients.length) summary.push(state.ingredients.length + ' Zutat' + (state.ingredients.length === 1 ? '' : 'en'));
    var textEl = $('#dvrm-resume-text');
    if (textEl) textEl.innerHTML = '<strong>Weitermachen wo du aufgehört hast?</strong> ' + summary.join(' · ');
    banner.style.display = 'flex';
  }

  function labelForRitualMoment(id) {
    var map = {
      'morning': 'Morgenritual',
      'daily_bowl': 'Täglicher Smoothie/Bowl Mix',
      'after_meal': 'Nach-dem-Essen Ritual',
      'evening_cacao': 'Kakao- oder Abendritual',
      'protein': 'Pflanzenprotein Mix',
      'tea': 'Tee- & Kräuterritual',
      'selfcare': 'Selfcare Ritual'
    };
    return map[id] || id;
  }
  function labelForBase(id) {
    var map = {
      'red_fruit': 'Fruchtig & rot',
      'cacao_spice': 'Kakao & Gewürze',
      'fiber_bowl': 'Faser & Bowl',
      'protein': 'Protein',
      'herbal_tea': 'Kräutertee',
      'green_daily': 'Grün & Daily'
    };
    return map[id] || id;
  }
  function labelForTaste(id) {
    var map = {
      'fruchtig': 'Fruchtig', 'schokoladig': 'Schokoladig', 'wuerzig': 'Würzig',
      'mild': 'Mild', 'frisch': 'Frisch', 'herb': 'Herb', 'saeuerlich': 'Säuerlich',
      'warm_aromatisch': 'Warm & aromatisch'
    };
    return map[id] || id;
  }

  // ─── Event wiring ─────────────────────────────────────
  function wireEvents() {
    // Landing CTA
    var startBtn = $('#dvrm-start');
    if (startBtn) startBtn.addEventListener('click', function (e) { e.preventDefault(); goTo('s2'); });

    // Back buttons (everywhere)
    $$('[data-back]').forEach(function (b) { b.addEventListener('click', goBack); });

    // Goal cards (Screen 2)
    $$('[data-goal]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.ritual_moment = b.getAttribute('data-goal');
        saveState();
        renderGoals();
      });
    });
    var goalNext = $('#dvrm-goal-next');
    if (goalNext) goalNext.addEventListener('click', function () { if (state.ritual_moment) goTo('s3'); });

    // Base cards (Screen 3)
    $$('[data-base]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.base = b.getAttribute('data-base');
        saveState();
        renderBases();
      });
    });
    var baseNext = $('#dvrm-base-next');
    if (baseNext) baseNext.addEventListener('click', function () { if (state.base) goTo('s4'); });

    // Taste cards (Screen 4)
    $$('[data-taste]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.taste = b.getAttribute('data-taste');
        saveState();
        renderTastes();
      });
    });
    var tasteNext = $('#dvrm-taste-next');
    if (tasteNext) tasteNext.addEventListener('click', function () { if (state.taste) goTo('s5'); });

    // Resume banner
    var resumeContinue = $('#dvrm-resume-continue');
    if (resumeContinue) resumeContinue.addEventListener('click', function () {
      var banner = $('#dvrm-resume');
      if (banner) banner.style.display = 'none';
      // Jump to the appropriate screen based on existing progress
      var target = 's2';
      if (state.blend_name) target = 's7';
      else if (state.ingredients.length) target = 's5';
      else if (state.taste) target = 's5';
      else if (state.base) target = 's4';
      else if (state.ritual_moment) target = 's3';
      goTo(target);
    });
    var resumeReset = $('#dvrm-resume-reset');
    if (resumeReset) resumeReset.addEventListener('click', function () {
      clearState();
      window.location.reload();
    });

    // ── Screen 5: Category chips (event delegation) ─────────────
    var catBar = $('#dvrm-cats');
    if (catBar) {
      catBar.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-cat]');
        if (!btn) return;
        state.category_filter = btn.getAttribute('data-cat');
        saveState();
        renderIngredients();
      });
    }

    // ── Screen 5: Ingredient grid (delegated) ───────────────────
    // Mobile UX: tapping ANYWHERE on the ingredient card toggles it.
    // The "Hinzufügen / Entfernen" button is just a visual affordance.
    // The "Mehr erfahren" button is the only escape hatch — it opens the
    // modal instead of toggling. This kills the "I tapped but nothing
    // happened" pattern caused by users missing the small toggle button.
    var grid = $('#dvrm-ings-grid');
    if (grid) {
      grid.addEventListener('click', function (e) {
        // "Mehr erfahren" → modal (do NOT toggle)
        var more = e.target.closest('[data-more]');
        if (more) {
          e.stopPropagation();
          openModal(more.getAttribute('data-more'));
          return;
        }
        // Either the explicit toggle button OR anywhere else on the card → toggle
        var toggle = e.target.closest('[data-toggle]');
        if (toggle) {
          toggleIngredient(toggle.getAttribute('data-toggle'));
          return;
        }
        var card = e.target.closest('.dvrm__ing[data-handle]');
        if (card && !card.classList.contains('is-locked')) {
          toggleIngredient(card.getAttribute('data-handle'));
        }
      });
    }

    // ── Modal close + focus trap ───────────────────────────────
    var modal = $('#dvrm-modal');
    if (modal) {
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-modal-close]')) closeModal();
      });
      document.addEventListener('keydown', function (e) {
        if (modal.hidden) return;
        if (e.key === 'Escape') { closeModal(); return; }
        trapModalFocus(e); // Tab/Shift+Tab stays inside the modal
      });
      var modalToggle = $('#dvrm-modal-toggle');
      if (modalToggle) {
        modalToggle.addEventListener('click', function () {
          var h = modalToggle.getAttribute('data-handle');
          if (!h) return;
          toggleIngredient(h);
          // Refresh modal state to reflect change (in-place — does
          // NOT reset _modalOpener so close still restores to the
          // original opener on the page).
          var savedOpener = _modalOpener;
          openModal(h);
          _modalOpener = savedOpener;
        });
      }
    }

    // ── Screen 5 → Screen 6 ─────────────────────────────────────
    var ingsNext = $('#dvrm-ings-next');
    if (ingsNext) ingsNext.addEventListener('click', function () {
      if (state.ingredients.length >= 3 && state.ingredients.length <= 6) goTo('s6');
    });

    // ── Screen 5: Reset selected ingredients (mobile escape hatch
    //              when state gets stuck or user wants a clean start) ──
    var ingsReset = $('#dvrm-ings-reset');
    if (ingsReset) ingsReset.addEventListener('click', function () {
      if (!state.ingredients.length) return;
      state.ingredients = [];
      saveState();
      renderIngredients();
      flashSelbar('Auswahl zurückgesetzt.');
    });

    // ── Screen 6: Intensity pills ───────────────────────────────
    var intensityBar = $('#dvrm-intensities');
    if (intensityBar) {
      intensityBar.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-intensity]');
        if (!btn) return;
        state.intensity = btn.getAttribute('data-intensity');
        saveState();
        renderBlendCheck();
      });
    }

    // ── Screen 6 → Screen 7 ─────────────────────────────────────
    var checkNext = $('#dvrm-check-next');
    if (checkNext) checkNext.addEventListener('click', function () {
      var st = computeStatus();
      if (st.status !== 'blocked') goTo('s7');
    });

    // ── Screen 7: Name + Wunschzutat inputs ─────────────────────
    var nameInput = $('#dvrm-name');
    if (nameInput) {
      nameInput.addEventListener('input', function () {
        state.blend_name = nameInput.value;
        saveState();
        renderNameForm();
      });
    }
    var wzInput = $('#dvrm-wunschzutat');
    if (wzInput) {
      wzInput.addEventListener('input', function () {
        state.wunschzutat = wzInput.value;
        saveState();
        renderNameForm();
      });
    }
    var suggest = $('#dvrm-name-suggest');
    if (suggest) suggest.addEventListener('click', function (e) {
      e.preventDefault();
      var s = pickSuggestedName();
      state.blend_name = s;
      saveState();
      if (nameInput) nameInput.value = s;
      renderNameForm();
    });
    var nameNext = $('#dvrm-name-next');
    if (nameNext) nameNext.addEventListener('click', function () {
      if (!nameNext.disabled) goTo('s8');
    });

    // ── Screen 8: Email + consents + submit ─────────────────────
    var emailInput = $('#dvrm-email');
    if (emailInput) {
      emailInput.addEventListener('input', function () {
        state.customer_email = emailInput.value;
        saveState();
        renderSubmitGate();
      });
    }
    var privacyChk = $('#dvrm-privacy');
    if (privacyChk) {
      privacyChk.addEventListener('change', function () {
        state.consent_privacy = privacyChk.checked;
        saveState();
        renderSubmitGate();
      });
    }
    var marketingChk = $('#dvrm-marketing');
    if (marketingChk) {
      marketingChk.addEventListener('change', function () {
        state.consent_marketing = marketingChk.checked;
        saveState();
      });
    }
    var submitBtn = $('#dvrm-submit-btn');
    if (submitBtn) submitBtn.addEventListener('click', submitRequest);
  }

  // ─── Init ────────────────────────────────────────────
  function init() {
    // ?reset=1 query param → nuke localStorage entirely. This is the
    // last-resort escape hatch the user can type/share if state ever
    // gets unrecoverable on their device.
    try {
      if (/[?&]reset=1\b/.test(location.search)) {
        clearState();
        state.screen = 's1';
        state.history = ['s1'];
        state.ritual_moment = null;
        state.base = null;
        state.taste = null;
        state.ingredients = [];
        state.intensity = 'ausgewogen';
        state.category_filter = 'all';
        state.blend_name = '';
        state.wunschzutat = '';
        state.consent_privacy = false;
        state.consent_marketing = false;
        state.customer_email = '';
        state.formula_id = null;
        state.submitted_at = null;
        saveState();
        console.info('[Ritualmix] state forcibly reset via ?reset=1');
      }
    } catch (_) {}
    // Prune orphan/legacy ingredient handles from saved state before any
    // render. Without this, a corrupted local state can pin the cap at 6
    // and silently block every "Hinzufügen" tap.
    pruneOrphanIngredients();
    showScreen(state.screen);
    updateProgress();
    renderGoals();
    renderBases();
    renderTastes();
    renderIngredients();
    renderBlendCheck();
    renderNameForm();
    renderSummary();
    renderSubmitGate();
    maybeShowResumeBanner();
    wireEvents();

    // Expose for M5 to extend and for debugging
    window.DVRM = {
      state: state,
      goTo: goTo,
      goBack: goBack,
      saveState: saveState,
      clearState: clearState,
      computeStatus: computeStatus,
      composeFormulaJSON: composeFormulaJSON,
      pickTemplateRatios: pickTemplateRatios,
      computeAllergens: computeAllergens,
      computeCaffeine: computeCaffeine,
      validateBlockedWords: validateBlockedWords,
      renderIngredients: renderIngredients,
      renderBlendCheck: renderBlendCheck,
      renderNameForm: renderNameForm,
      renderSummary: renderSummary,
      renderSubmitGate: renderSubmitGate,
      submitRequest: submitRequest,
      toggleIngredient: toggleIngredient,
      openModal: openModal,
      closeModal: closeModal,
      BLOCKED_WORDS: BLOCKED_WORDS,
      DVRM_FEE_VARIANT_ID: DVRM_FEE_VARIANT_ID
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
